import { useEffect, useReducer } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useConnectorSchedule, useUpdateSchedule } from '@/hooks/useConnectors';
import { SchedulePreview } from '@/components/connectors/schedule/SchedulePreview';
import { TIMEZONES } from '@/components/connectors/schedule/timezones';
import { DEFAULT_SCHEDULE_TIMEZONE } from '@/constants/timezone';
import { useToast } from '@/hooks/useToast';
import {
  ScheduleFrequencyForm,
  type FormState,
  formStateToCron,
  presetToFormState,
} from './ScheduleFrequencyForm';
import { cronExpressionToPreset } from './cronExpressionToPreset';
import type { ScheduleUpdateRequest } from '@/types/connectors';

interface Props { connectorType: string; name: string; }

interface TabState {
  initial: FormState;
  value: FormState;
  initialTz: string;
  timezone: string;
}

type Action =
  | { type: 'SEED'; initial: FormState; timezone: string }
  | { type: 'SET_FORM'; value: FormState }
  | { type: 'SET_TZ'; timezone: string };

function reducer(state: TabState, action: Action): TabState {
  switch (action.type) {
    case 'SEED':
      return { initial: action.initial, value: action.initial, initialTz: action.timezone, timezone: action.timezone };
    case 'SET_FORM':
      return { ...state, value: action.value };
    case 'SET_TZ':
      return { ...state, timezone: action.timezone };
    default:
      return state;
  }
}

const initState: TabState = { initial: null, value: null, initialTz: DEFAULT_SCHEDULE_TIMEZONE, timezone: DEFAULT_SCHEDULE_TIMEZONE };

export function ScheduleTab({ connectorType, name }: Props) {
  const { data: schedule } = useConnectorSchedule(connectorType, name);
  const update = useUpdateSchedule();
  const { toast } = useToast();
  const [state, dispatch] = useReducer(reducer, initState);
  const { initial, value, initialTz, timezone } = state;

  const cronExpr  = schedule?.cron_expression ?? null;
  const schedTz   = schedule?.timezone ?? null;
  const enabled   = schedule?.enabled ?? null;

  useEffect(() => {
    if (enabled === null) return;
    const tz = schedTz || DEFAULT_SCHEDULE_TIMEZONE;
    if (!enabled) {
      dispatch({ type: 'SEED', initial: { frequency: 'off' }, timezone: tz });
      return;
    }
    const preset = cronExpressionToPreset(cronExpr);
    const seeded = presetToFormState(preset);
    dispatch({ type: 'SEED', initial: seeded, timezone: tz });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cronExpr, schedTz, enabled]);

  // The preview must reflect the PENDING edit, not the last saved schedule (#1020).
  // Deriving it here — the same way onSave does — keeps the cron and timezone props
  // fed from the same source; reading cron off `schedule` while timezone came from
  // form state meant a cadence change never retriggered the preview query, whose key
  // includes the cron. Null for Off/unset, which also gates the block below.
  const previewCron = formStateToCron(value);

  const formChanged = JSON.stringify(value) !== JSON.stringify(initial);
  const tzChanged   = timezone !== initialTz;
  const dirty       = formChanged || tzChanged;
  const isOff       = value?.frequency === 'off';
  // Show the "scheduling is disabled" message only when the SAVED state is Off
  // and the user hasn't started editing yet. Once they're transitioning to or
  // from Off, the form should look like a normal pending edit.
  const isSavedOff  = isOff && initial?.frequency === 'off';

  const onSave = async () => {
    // When turning the schedule Off, omit cron_expression entirely so the
    // backend's `is not None` check leaves the existing cron alone in the DB.
    // We only want to flip schedule_enabled to false in that case.
    const payload: ScheduleUpdateRequest = isOff
      ? { enabled: false, timezone }
      : {
          enabled: true,
          cron_expression: formStateToCron(value) ?? '',
          timezone,
        };
    try {
      await update.mutateAsync({
        connectorType: connectorType as any,
        name,
        schedule: payload,
      });
      toast({
        title: 'Schedule saved',
        description: isOff
          ? 'Scheduling has been turned off.'
          : 'Scheduled runs will use the new settings.',
      });
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="rounded-lg border bg-card">
      <div className="border-b px-5 py-4">
        <h3 className="text-base font-semibold">Run schedule</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Choose how often this connector extracts automatically. Manual runs are always available.
        </p>
      </div>
      <div className="space-y-6 p-5">
      {/* Frequency form (no internal Save — rendered below the timezone). */}
      <ScheduleFrequencyForm
        value={value}
        onChange={(v) => dispatch({ type: 'SET_FORM', value: v })}
      />

      {isSavedOff ? (
        <p className="text-sm text-muted-foreground">
          Scheduling is currently disabled. Pick a frequency above to enable it.
        </p>
      ) : isOff ? null : (
        <div className="grid grid-cols-[120px_1fr] items-center gap-2 max-w-md">
          <Label htmlFor="timezone-select">Timezone</Label>
          <Select value={timezone} onValueChange={(v) => dispatch({ type: 'SET_TZ', timezone: v })}>
            <SelectTrigger id="timezone-select" aria-label="Timezone">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map(([v, label]) => (
                <SelectItem key={v} value={v}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Button type="button" onClick={onSave} disabled={!dirty || update.isPending}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {!isOff && (
        <div>
          <div className="text-xs text-muted-foreground uppercase mb-2">Next runs</div>
          {previewCron && (
            <SchedulePreview
              connectorType={connectorType}
              connectorName={name}
              cronExpression={previewCron}
              timezone={timezone}
              count={5}
            />
          )}
        </div>
      )}
      </div>
    </div>
  );
}
